import React, { useEffect, useMemo, useRef, useState } from 'react';
import { merchantApi } from '../../api/config';

export type OrderRefType = 'order' | 'payout' | 'settlement';

export interface OrderRef {
  ref_type: OrderRefType;
  ref_id: string;
  ref_number: string;
  ref_amount?: number | null;
  ref_status?: string | null;
  ref_created_at?: string | null;
}

interface Props {
  value: OrderRef[];
  onChange: (next: OrderRef[]) => void;
  maxItems?: number;
}

const TYPE_LABEL: Record<OrderRefType, string> = {
  order: 'Order (Payin)',
  payout: 'Payout',
  settlement: 'Settlement',
};

const TYPE_COLOR: Record<OrderRefType, string> = {
  order: 'bg-blue-100 text-blue-700 border-blue-200',
  payout: 'bg-purple-100 text-purple-700 border-purple-200',
  settlement: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const STATUS_COLOR: Record<string, string> = {
  pending: 'text-amber-700 bg-amber-50',
  paid: 'text-emerald-700 bg-emerald-50',
  completed: 'text-emerald-700 bg-emerald-50',
  approved: 'text-emerald-700 bg-emerald-50',
  rejected: 'text-rose-700 bg-rose-50',
  cancelled: 'text-slate-700 bg-slate-50',
  expired: 'text-slate-700 bg-slate-50',
  failed: 'text-rose-700 bg-rose-50',
};

const OrderMultiSelect: React.FC<Props> = ({ value, onChange, maxItems = 20 }) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [types, setTypes] = useState<Record<OrderRefType, boolean>>({ order: true, payout: true, settlement: true });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<OrderRef[]>([]);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const activeTypes = useMemo(
    () => (Object.keys(types) as OrderRefType[]).filter(t => types[t]),
    [types],
  );

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoading(true);
    merchantApi
      .get('/tickets/search-orders', {
        params: { q: debouncedQuery, types: activeTypes.join(',') },
        signal: controller.signal as any,
      })
      .then(res => setResults(res.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [debouncedQuery, activeTypes, open]);

  const selectedKey = (r: OrderRef) => `${r.ref_type}:${r.ref_id}`;
  const selectedKeys = useMemo(() => new Set(value.map(selectedKey)), [value]);

  const grouped = useMemo(() => {
    const g: Record<OrderRefType, OrderRef[]> = { order: [], payout: [], settlement: [] };
    for (const r of results) g[r.ref_type].push(r);
    return g;
  }, [results]);

  function toggle(r: OrderRef) {
    const key = selectedKey(r);
    if (selectedKeys.has(key)) {
      onChange(value.filter(v => selectedKey(v) !== key));
    } else {
      if (value.length >= maxItems) return;
      onChange([...value, r]);
    }
  }

  function remove(r: OrderRef) {
    onChange(value.filter(v => selectedKey(v) !== selectedKey(r)));
  }

  return (
    <div className="space-y-2" ref={wrapRef}>
      <div className="flex flex-wrap gap-1.5">
        {(Object.keys(types) as OrderRefType[]).map(t => (
          <button
            key={t}
            type="button"
            onClick={() => setTypes(prev => ({ ...prev, [t]: !prev[t] }))}
            className={`px-2.5 py-1 text-xs rounded-full border transition ${
              types[t] ? TYPE_COLOR[t] : 'bg-white text-slate-400 border-slate-200'
            }`}
          >
            {types[t] ? '✓ ' : ''}{TYPE_LABEL[t]}
          </button>
        ))}
      </div>

      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="พิมพ์เลข order / payout / id settlement เพื่อค้นหา..."
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 bg-white"
        />

        {open && (
          <div className="absolute z-30 mt-1 left-0 right-0 max-h-80 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg">
            {loading && <div className="p-3 text-xs text-slate-500">กำลังค้นหา...</div>}
            {!loading && results.length === 0 && (
              <div className="p-3 text-xs text-slate-500">ไม่พบรายการที่ตรง</div>
            )}
            {!loading && (Object.keys(grouped) as OrderRefType[]).map(t => (
              grouped[t].length === 0 ? null : (
                <div key={t} className="border-b border-slate-100 last:border-b-0">
                  <div className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold ${TYPE_COLOR[t].replace('border-','').replace('-200','-50')}`}>
                    {TYPE_LABEL[t]}
                  </div>
                  {grouped[t].map(r => {
                    const sel = selectedKeys.has(selectedKey(r));
                    return (
                      <button
                        key={selectedKey(r)}
                        type="button"
                        onClick={() => toggle(r)}
                        className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-slate-50 ${sel ? 'bg-blue-50' : ''}`}
                      >
                        <span className={`inline-block w-3 h-3 border rounded ${sel ? 'bg-blue-500 border-blue-500' : 'bg-white border-slate-300'}`} />
                        <span className="font-mono text-slate-800 flex-1">{r.ref_number}</span>
                        {typeof r.ref_amount === 'number' && (
                          <span className="text-slate-600">฿{Number(r.ref_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
                        )}
                        {r.ref_status && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] ${STATUS_COLOR[r.ref_status] || 'bg-slate-100 text-slate-600'}`}>
                            {r.ref_status}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )
            ))}
          </div>
        )}
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {value.map(r => (
            <span
              key={`${r.ref_type}:${r.ref_id}`}
              className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-full border ${TYPE_COLOR[r.ref_type]}`}
            >
              <span className="font-semibold uppercase text-[9px]">{r.ref_type}</span>
              <span className="font-mono">{r.ref_number}</span>
              <button
                type="button"
                onClick={() => remove(r)}
                className="ml-1 hover:text-rose-600"
                title="ลบ"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrderMultiSelect;
